import { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/services/auth/AuthContext";
import getApiEndpoint from "@/services/api/endpoint";
import { getBaseUrl } from "@/services/api/baseUrl";
import useWebSocket, { ReadyState } from "react-use-websocket";

interface UseAPISubscriptionOptions {
  onMessage?: (data: any) => void;
  onError?: (error: Event) => void;
  queryKey?: string[];
}

export const useAPISubscription = (
  endpoint: string,
  options: UseAPISubscriptionOptions = {}
): {
  isConnected: boolean;
  sendMessage: (message: any) => void;
} => {
  const [isWebSocketConnected, setIsWebSocketConnected] =
    useState<boolean>(false);
  const { token, tenantId, tokenExpiry } = useAuth();
  const queryClient = useQueryClient();

  // When token changes (JWT refresh), this callback reference changes,
  // which triggers react-use-websocket to reconnect with the new token
  const getWebSocketUrl = useCallback(() => {
    if (!token) {
      console.error("No token found. Unable to connect to WebSocket.");
      return null;
    }

    if (tokenExpiry < Date.now()) {
      console.warn("Token has expired. Unable to connect to WebSocket.");
      return null;
    }

    const wsEndpoint = getApiEndpoint(endpoint).replace(/^\//, "");
    return `${getBaseUrl().replace(/^http/, "ws")}/${wsEndpoint}`;
  }, [token, tokenExpiry, endpoint]);

  const { sendJsonMessage, readyState } = useWebSocket(getWebSocketUrl(), {
    protocols:
      token && tenantId
        ? ["Authorization", token, "X-Tenant-Id", tenantId]
        : undefined,
    // We'll keep the close code here but we'll never reach since, for security reasons,
    // we don't accept connections at all and then always get code 1006, which isn't necessarily 401.
    shouldReconnect: (closeEvent) => ![4001].includes(closeEvent.code),
    reconnectInterval: 3000,
    reconnectAttempts: Number.MAX_SAFE_INTEGER, // Never stop reconnecting
    // Built-in heartbeat: if no message received within timeout, connection is closed and reconnected
    heartbeat: {
      message: "ping",
      returnMessage: "pong",
      timeout: 30000, // 30 seconds without response triggers reconnect
      interval: 15000 // Send ping every 15 seconds
    },
    onOpen: () => {
      setIsWebSocketConnected(true);
    },
    onClose: (_event) => {
      setIsWebSocketConnected(false);
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    onMessage: (event) => {
      // Ignore heartbeat acks
      if (event.data === "pong") return;

      let finalMessage;
      try {
        const parsed = JSON.parse(event.data);
        // If message is a string, parse it again (double-encoded JSON)
        finalMessage = typeof parsed === "string" ? JSON.parse(parsed) : parsed;
      } catch {
        // Not JSON — ignore
        return;
      }

      options.onMessage?.(finalMessage);
      // Invalidate and refetch the related query if queryKey is provided
      if (options.queryKey) {
        queryClient.invalidateQueries({ queryKey: options.queryKey });
      }
    }
  });

  const sendWebSocketMessage = useCallback(
    (message: any) => {
      sendJsonMessage(message);
    },
    [sendJsonMessage]
  );

  const isConnected = useMemo(
    () => isWebSocketConnected && readyState === ReadyState.OPEN,
    [isWebSocketConnected, readyState]
  );

  return { isConnected, sendMessage: sendWebSocketMessage };
};
